import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useNavigate } from '@tanstack/react-router';
import { Button, EmptyState, ErrorState, LoadingState, ThemeToggle } from '@lawbee/ui';
import { LockKeyhole, LogOut } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { PoweredBy } from './Logo';

export type PortalMe = {
  contact: { firstName: string; lastName: string; email: string };
  firm: { name: string; slug: string; confidentialityNotice: string };
};

export const PORTAL_ME_KEY = ['portal', 'me'] as const;
const LAST_SLUG_KEY = 'lb-portal-slug';

export function rememberPortalSlug(slug: string) {
  try {
    localStorage.setItem(LAST_SLUG_KEY, slug);
  } catch {
    // storage unavailable
  }
}

function lastPortalSlug(): string | null {
  try {
    return localStorage.getItem(LAST_SLUG_KEY);
  } catch {
    return null;
  }
}

export function usePortalMe() {
  return useQuery({
    queryKey: PORTAL_ME_KEY,
    queryFn: async () => {
      try {
        return await api.get<PortalMe>('/portal-api/me');
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
}

/** Signed-in client portal frame: firm header, sign out, "not legal advice" footer. */
export function PortalAppLayout() {
  const me = usePortalMe();
  const qc = useQueryClient();
  const navigate = useNavigate();

  if (me.isLoading) return <LoadingState label="Opening your portal" className="min-h-dvh" />;
  if (me.error) return <ErrorState className="m-8" error={me.error} onRetry={() => void me.refetch()} />;
  if (!me.data) {
    const slug = lastPortalSlug();
    return (
      <div className="flex min-h-dvh items-center justify-center px-4">
        <EmptyState
          icon={<LockKeyhole />}
          title="Your portal session has ended"
          description="For your privacy, portal sessions expire. Request a new sign-in link to continue."
          action={
            slug ? (
              <Button asChild>
                <Link to="/portal/$slug" params={{ slug }}>
                  Get a new link
                </Link>
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">Use the portal link your law firm sent you.</p>
            )
          }
        />
      </div>
    );
  }
  rememberPortalSlug(me.data.firm.slug);

  const signOut = async () => {
    try {
      await api.post('/portal-api/logout');
    } catch {
      // already signed out
    }
    qc.removeQueries({ queryKey: ['portal'] });
    await navigate({ to: '/portal/$slug', params: { slug: me.data!.firm.slug } });
  };

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <header className="sticky top-0 z-30 border-b bg-card/95 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-4xl items-center gap-3 px-4 sm:px-6">
          <Link to="/portal/app" className="min-w-0">
            <span className="block truncate text-[15px] font-semibold">{me.data.firm.name}</span>
            <span className="block truncate text-xs text-muted-foreground">Client portal</span>
          </Link>
          <div className="ml-auto flex items-center gap-1">
            <span className="mr-2 hidden text-sm text-muted-foreground sm:inline">
              {me.data.contact.firstName} {me.data.contact.lastName}
            </span>
            <ThemeToggle />
            <Button variant="ghost" size="sm" onClick={() => void signOut()}>
              <LogOut /> Sign out
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-4xl flex-1 px-4 py-8 sm:px-6">
        <Outlet />
      </main>
      <footer className="px-4 pb-8 text-center text-xs text-muted-foreground">
        <p>Status updates are not legal advice. Contact your lawyer with any questions about your matter.</p>
        <p className="mt-2 inline-flex items-center gap-1.5">
          <PoweredBy />
        </p>
      </footer>
    </div>
  );
}
